import "@maersk-global/mds-components-core-input";
import "@maersk-global/mds-components-core-textarea";
import { MdsConfig } from "@maersk-global/mds-config";
import "./fields.css";

export const PHASE5_VERSION = "mds-input-textarea-mirrors";

const FIELD_DEFINITIONS = [
  { name: "shipper_name", tagName: "mc-input", kind: "input" },
  { name: "consignee_name", tagName: "mc-input", kind: "input" },
  { name: "notify_name", tagName: "mc-input", kind: "input" },
  { name: "marks_notes", tagName: "mc-textarea", kind: "textarea" }
] as const;

type NativeField = HTMLInputElement | HTMLTextAreaElement;
type MdsField = HTMLElement & {
  value?: unknown;
  label?: string;
  invalid?: boolean;
  errormessage?: string;
  disabled?: boolean;
  required?: boolean;
  readonly?: boolean;
  placeholder?: string;
  maxlength?: number;
  minlength?: number;
  rows?: number;
};

type FieldDefinition = (typeof FIELD_DEFINITIONS)[number];

export interface Phase5FieldBinding {
  name: string;
  kind: FieldDefinition["kind"];
  mds: MdsField;
  native: NativeField;
  wrapper: HTMLLabelElement;
}

export interface Phase5Contract {
  installed: boolean;
  version: string;
  fieldNames: string[];
  bindings: Phase5FieldBinding[];
}

function configureMdsRuntime(documentRef: Document): void {
  const isViteDevServer = Boolean(documentRef.querySelector('script[src="/@vite/client"]'));
  MdsConfig.iconsDynamicImportPath = isViteDevServer
    ? "/node_modules/"
    : new URL("./assets/node_modules/", documentRef.baseURI).pathname;
}

function normalizeValue(value: unknown): string {
  return value === null || value === undefined ? "" : String(value);
}

function getMdsValue(field: MdsField): string {
  return normalizeValue(field.value);
}

function getLabelSpan(wrapper: HTMLLabelElement, documentRef: Document, fallback: string): HTMLElement {
  const existing = wrapper.querySelector<HTMLElement>(".label-text");
  if (existing) {
    existing.classList.add("phase5-legacy-label");
    // Keep one visible, outer label in the legacy grid. The MDS field keeps
    // the same text as its accessible aria-label, but hides its own visual
    // label so the field control lands on the same row as its native peers.
    existing.removeAttribute("aria-hidden");
    existing.hidden = false;
    return existing;
  }

  const textNode = Array.from(wrapper.childNodes).find(
    node => node.nodeType === Node.TEXT_NODE && Boolean(node.textContent?.trim()),
  );
  const labelSpan = documentRef.createElement("span");
  labelSpan.className = "label-text phase5-legacy-label";
  labelSpan.textContent = textNode?.textContent?.trim() || fallback;
  if (textNode) {
    textNode.replaceWith(labelSpan);
  } else {
    wrapper.insertBefore(labelSpan, wrapper.firstChild);
  }
  return labelSpan;
}

function getLabelText(labelSpan: HTMLElement, fallback: string): string {
  return labelSpan.textContent?.trim() || fallback;
}

function setMdsProperty(field: MdsField, propertyName: string, value: unknown): void {
  (field as unknown as Record<string, unknown>)[propertyName] = value;
}

function copyNativeAttributes(native: NativeField, mds: MdsField, definition: FieldDefinition, label: string): void {
  if (native.id) {
    mds.id = native.id;
    native.setAttribute("data-phase5-original-id", native.id);
    native.removeAttribute("id");
  }

  mds.setAttribute("name", definition.name);
  mds.setAttribute("label", label);
  // The outer legacy label remains visible to keep every column aligned.
  // `hiddenlabel` is the documented MDS way to retain the accessible label
  // while removing the component's duplicate visual label.
  mds.setAttribute("hiddenlabel", "");
  mds.setAttribute("data-phase5-field", definition.name);
  mds.classList.add("phase5-mds-field");
  setMdsProperty(mds, "value", native.value);

  if (definition.kind === "input") {
    const input = native as HTMLInputElement;
    if (input.type && input.type !== "text") {
      mds.setAttribute("type", input.type);
    }
  } else {
    const textarea = native as HTMLTextAreaElement;
    if (textarea.rows) {
      mds.setAttribute("rows", String(textarea.rows));
    }
  }

  if (native.required) {
    mds.setAttribute("required", "");
  }
  if (native.disabled) {
    mds.setAttribute("disabled", "");
  }
  if (native.readOnly) {
    mds.setAttribute("readonly", "");
  }
  if (native.placeholder) {
    mds.setAttribute("placeholder", native.placeholder);
  }
  if (native.maxLength >= 0) {
    mds.setAttribute("maxlength", String(native.maxLength));
  }
  if (native.minLength > 0) {
    mds.setAttribute("minlength", String(native.minLength));
  }
}

function dispatchMirrorEvent(native: NativeField, eventName: "input" | "change" | "focus" | "blur"): void {
  const event = eventName === "focus" || eventName === "blur"
    ? new FocusEvent(eventName, { bubbles: true, composed: true })
    : new Event(eventName, { bubbles: true, composed: true });
  native.dispatchEvent(event);
}

function clearLegacyInvalidState(native: NativeField): void {
  native.classList.remove("invalid");
  native.closest("label")?.classList.remove("invalid-label");
}

function setLegacyInvalidState(native: NativeField, invalid: boolean): void {
  native.classList.toggle("invalid", invalid);
  native.closest("label")?.classList.toggle("invalid-label", invalid);
}

function queueMicrotaskCompat(callback: () => void): void {
  if (typeof queueMicrotask === "function") {
    queueMicrotask(callback);
    return;
  }
  void Promise.resolve().then(callback);
}

function syncNativeStateToMds(binding: Phase5FieldBinding): void {
  const { native, mds } = binding;
  const invalid = native.classList.contains("invalid") || native.getAttribute("aria-invalid") === "true";
  setMdsProperty(mds, "invalid", invalid);
  if (!invalid) {
    mds.classList.remove("invalid");
  }
  setMdsProperty(mds, "disabled", native.disabled);
  setMdsProperty(mds, "required", native.required);
  setMdsProperty(mds, "readonly", native.readOnly);
  if (native.placeholder !== normalizeValue(mds.placeholder)) {
    setMdsProperty(mds, "placeholder", native.placeholder);
  }
}

function syncMdsInvalidToNative(binding: Phase5FieldBinding): void {
  const { native, mds } = binding;
  const invalid = Boolean(mds.invalid) || mds.classList.contains("invalid") || mds.getAttribute("aria-invalid") === "true";
  setLegacyInvalidState(native, invalid);
  if (Boolean(mds.invalid) !== invalid) {
    setMdsProperty(mds, "invalid", invalid);
  }
}

function syncMdsValueToNative(binding: Phase5FieldBinding, dispatchInput = false): boolean {
  const { native, mds } = binding;
  const value = getMdsValue(mds);
  if (native.value === value) {
    return false;
  }
  native.value = value;
  if (dispatchInput) {
    dispatchMirrorEvent(native, "input");
  }
  return true;
}

function syncNativeValueToMds(binding: Phase5FieldBinding): void {
  const { native, mds } = binding;
  const value = native.value;
  if (getMdsValue(mds) !== value) {
    setMdsProperty(mds, "value", value);
  }
}

function installBindingObservers(binding: Phase5FieldBinding, labelSpan: HTMLElement): void {
  const { native, mds, wrapper } = binding;
  let shadowEventsBound = false;

  const handleMdsValueEvent = (eventName: "input" | "change"): void => {
    clearLegacyInvalidState(native);
    mds.classList.remove("invalid");
    setMdsProperty(mds, "invalid", false);
    const changed = syncMdsValueToNative(binding, true);
    if (!changed && eventName === "change") {
      dispatchMirrorEvent(native, "change");
    }
  };

  const syncLabel = (): void => {
    const nextLabel = getLabelText(labelSpan, binding.name);
    if (normalizeValue(mds.label) !== nextLabel) {
      mds.setAttribute("label", nextLabel);
      setMdsProperty(mds, "label", nextLabel);
    }
  };

  const mdsObserver = new MutationObserver(() => {
    syncMdsValueToNative(binding);
    syncLabel();
    syncMdsInvalidToNative(binding);
  });
  mdsObserver.observe(mds, { attributes: true, childList: true, subtree: true });

  const nativeObserver = new MutationObserver(() => {
    syncNativeStateToMds(binding);
    queueMicrotaskCompat(() => syncNativeValueToMds(binding));
  });
  nativeObserver.observe(native, {
    attributes: true,
    attributeFilter: ["aria-invalid", "class", "disabled", "maxlength", "minlength", "placeholder", "readonly", "required"]
  });

  const labelObserver = new MutationObserver(syncLabel);
  labelObserver.observe(wrapper, { characterData: true, childList: true, subtree: true });

  mds.addEventListener("input", () => handleMdsValueEvent("input"));
  mds.addEventListener("change", () => handleMdsValueEvent("change"));

  // MDS forwards the native input event from its shadow root, but the event
  // is not guaranteed to be retargeted to the host by every browser build.
  // Listening at the shadow root keeps the legacy mirror contract stable.
  const bindShadowValueEvents = (): void => {
    const shadowRoot = mds.shadowRoot;
    if (!shadowRoot || shadowEventsBound) {
      return;
    }
    shadowEventsBound = true;
    shadowRoot.addEventListener("input", () => handleMdsValueEvent("input"));
    shadowRoot.addEventListener("change", () => handleMdsValueEvent("change"));
    shadowRoot.addEventListener("focus", () => dispatchMirrorEvent(native, "focus"), true);
    shadowRoot.addEventListener("blur", () => dispatchMirrorEvent(native, "blur"), true);
  };
  bindShadowValueEvents();
  queueMicrotaskCompat(bindShadowValueEvents);

  ["focus", "blur"].forEach(eventName => {
    mds.addEventListener(eventName, () => dispatchMirrorEvent(native, eventName as "focus" | "blur"));
  });

  ["input", "change", "blur"].forEach(eventName => {
    native.addEventListener(eventName, () => {
      queueMicrotaskCompat(() => syncNativeValueToMds(binding));
    });
  });

  syncLabel();
  syncNativeStateToMds(binding);
  syncMdsValueToNative(binding);
}

function createBinding(
  form: HTMLFormElement,
  definition: FieldDefinition,
  documentRef: Document,
): Phase5FieldBinding | null {
  const selector = `input[name="${definition.name}"]:not([data-phase5-native-mirror]), textarea[name="${definition.name}"]:not([data-phase5-native-mirror])`;
  const native = form.querySelector<NativeField>(selector);
  if (!native) {
    return null;
  }
  const wrapper = native.closest("label");
  if (!wrapper) {
    return null;
  }

  const labelSpan = getLabelSpan(wrapper, documentRef, definition.name);
  const mds = documentRef.createElement(definition.tagName) as MdsField;
  copyNativeAttributes(native, mds, definition, getLabelText(labelSpan, definition.name));

  native.classList.add("phase5-native-mirror");
  native.setAttribute("data-phase5-native-mirror", definition.name);
  native.setAttribute("aria-hidden", "true");
  native.tabIndex = -1;
  native.hidden = true;
  wrapper.insertBefore(mds, native);

  const binding: Phase5FieldBinding = { name: definition.name, kind: definition.kind, mds, native, wrapper };
  installBindingObservers(binding, labelSpan);
  return binding;
}

export function installPhase5Fields(documentRef: Document = document): Phase5Contract {
  if (!documentRef?.body) {
    return { installed: false, version: PHASE5_VERSION, fieldNames: [], bindings: [] };
  }

  const form = documentRef.querySelector<HTMLFormElement>("#draftForm");
  if (!form) {
    return { installed: false, version: PHASE5_VERSION, fieldNames: [], bindings: [] };
  }

  configureMdsRuntime(documentRef);
  documentRef.body.classList.add("phase5-fields");
  const bindings = FIELD_DEFINITIONS.map(definition => createBinding(form, definition, documentRef)).filter(
    (binding): binding is Phase5FieldBinding => Boolean(binding),
  );
  const contract: Phase5Contract = {
    installed: bindings.length === FIELD_DEFINITIONS.length,
    version: PHASE5_VERSION,
    fieldNames: bindings.map(binding => binding.name),
    bindings,
  };

  return contract;
}

const phase5Contract =
  typeof document === "undefined"
    ? { installed: false, version: PHASE5_VERSION, fieldNames: [], bindings: [] }
    : installPhase5Fields(document);

(globalThis as typeof globalThis & { __newformsPhase5?: Phase5Contract }).__newformsPhase5 = phase5Contract;
